import type { AddedAddOn } from '@/ducks/addOns/types';

import { trackEvent } from '@/tagmanager/dataLayer';

import type {
  CommonTrackingParams,
  TrackingEcommerceItem,
  TrackingEventData,
  TrackingParamsWithAddOns,
} from './shared';

export const createCruisePackageItem = (params: CommonTrackingParams): TrackingEcommerceItem => {
  const formattedTravelDates = `${params.embarkDate} - ${params.debarkDate}`;

  return {
    cabin_id: params.cabinId,
    cabin_name: params.cabinName,
    discount: params.discount || 0,
    item_category: 'Cruise Package',
    item_id: params.packageId,
    item_name: params.packageName,
    nights: params.duration,
    number_passengers: params.numberPassengers,
    ports: params.ports,
    price: parseFloat(params.cruisePrice.toFixed(2)),
    promotion_id: params.promotionId || 'N/A',
    promotion_name: params.promotionName || 'N/A',
    quantity: 1,
    ship_name: params.shipName,
    travel_dates: formattedTravelDates,
  };
};

export const createAddOnItems = (addedAddOns: AddedAddOn[]): TrackingEcommerceItem[] => {
  return addedAddOns.map((addOn) => ({
    item_category: 'Add-on',
    item_id: addOn.code,
    item_name: addOn.name,
    price: addOn.price || 0,
    quantity: addOn.quantity,
  }));
};

export const createAddOnItemsWithRelatedId = (
  addedAddOns: AddedAddOn[],
  relatedItemId: string | null,
): TrackingEcommerceItem[] => {
  return addedAddOns.map((addOn) => ({
    item_category: 'Add-on',
    item_id: addOn.code,
    item_name: addOn.name,
    price: addOn.price || 0,
    quantity: addOn.quantity,
    related_item_id: relatedItemId || 'N/A',
  }));
};

export const createCompleteEcommerceItems = (params: TrackingParamsWithAddOns): TrackingEcommerceItem[] => {
  const cruiseItem = createCruisePackageItem(params);
  const addOnItems = createAddOnItems(params.addedAddOns);

  return [cruiseItem, ...addOnItems];
};

export const normalizeItineraryGroup = (itineraryGroup: string | null): string | string[] => {
  return itineraryGroup ? [itineraryGroup] : 'N/A';
};

export const normalizeRegions = (regions: string[] | null): string[] | string => {
  return regions || 'N/A';
};

export const createBaseTrackingEvent = (
  eventName: string,
  params: TrackingParamsWithAddOns,
  items: TrackingEcommerceItem[],
): TrackingEventData => {
  return {
    addons_value: params.addonsValue || 0,
    coupon_code: params.couponCode || 'N/A',
    ecommerce: {
      currency: params.currencyCode,
      items,
      tax: params.tax || 0,
      value: params.totalValue,
    },
    event: eventName,
    itinerary_group: normalizeItineraryGroup(params.itinerary_group),
    port_fees: params.portFees,
    regions: normalizeRegions(params.regions),
    selected_tier: params.fareClass,
  };
};

export const trackEcommerceEvent = (eventData: TrackingEventData): void => {
  trackEvent(eventData);
};
